import { useCallback, useEffect, useState } from "react";
import { ExternalLink, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, SectionBadge, Pill } from "@/components/Badges";
import { api, formatDate, SECTIONS } from "@/lib/api";
import { openDocument } from "@/lib/openDocument";
import { useQuickCreate } from "@/context/QuickCreate";

export default function Completed() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("all");
  const { refreshKey } = useQuickCreate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks", { params: { status: "Tamamlandı", section } });
      setTasks(data);
    } finally {
      setLoading(false);
    }
  }, [section, refreshKey]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Tamamlananlar</h1>
        <p className="mt-1 text-sm text-muted-foreground">{tasks.length} tamamlanmış kayıt</p>
      </div>

      <Select value={section} onValueChange={setSection}>
        <SelectTrigger className="w-full sm:w-[220px]" data-testid="completed-filter-section">
          <SelectValue placeholder="Bölüm" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tüm Bölümler</SelectItem>
          {SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>

      {loading && (
        <div className="space-y-3" data-testid="completed-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[72px] animate-pulse rounded-xl border border-border bg-card" />
          ))}
        </div>
      )}

      {!loading && !tasks.length && (
        <div className="rounded-xl border border-border bg-card p-8 text-center shadow-shb" data-testid="completed-empty">
          <p className="text-sm text-muted-foreground">Henüz tamamlanmış kayıt yok.</p>
        </div>
      )}

      <div className="space-y-3" data-testid="completed-list">
        {tasks.map((t) => (
          <div
            key={t.id}
            data-testid={`completed-item-${t.id}`}
            className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-shb sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex min-w-0 items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <div className="min-w-0">
                <p className="truncate font-semibold text-foreground">{t.title}</p>
                {t.description && <p className="truncate text-sm text-muted-foreground">{t.description}</p>}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <SectionBadge section={t.section} />
                  <StatusBadge status={t.status} />
                  {t.folder_not_found && (
                    <Pill className="border-crimson/25 bg-crimson/10 text-crimson dark:text-red-300">
                      Dosya klasörü bulunamadı
                    </Pill>
                  )}
                  <span className="font-mono text-xs text-muted-foreground">{formatDate(t.updated_at)}</span>
                </div>
              </div>
            </div>
            {(t.drive_link || t.drive_file_id) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => openDocument({ taskId: t.id })}
                data-testid={`completed-view-document-${t.id}`}
              >
                <ExternalLink className="h-4 w-4" /> Evrakı Görüntüle
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
